import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSettings } from "@/lib/SettingsContext";
import usePageMeta from "@/lib/usePageMeta";
import { Link } from "react-router-dom";
import { ArrowRight, Camera } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const resolveUrl = (u) => (u && u.startsWith("/") ? `${process.env.REACT_APP_BACKEND_URL}${u}` : u);

export default function Gallery() {
  usePageMeta(
    "Gallery · Beautify — Wadebridge, Cornwall",
    "A look inside Deborah Louise's calm beauty & massage studios in Wadebridge, Cornwall — treatments, results and the spaces where you'll unwind."
  );
  const { brand: BRAND } = useSettings();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setItems(res.data || []);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const categories = useMemo(() => {
    const set = new Set(["All"]);
    items.forEach((i) => i.category && set.add(i.category));
    return [...set];
  }, [items]);

  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);

  return (
    <div data-testid="gallery-page" className="bg-grain">
      <div className="mx-auto max-w-7xl px-5 sm:px-6 lg:px-10 py-10 sm:py-16">
        <header className="text-center mb-10 sm:mb-12">
          <span className="text-xs tracking-[0.3em] uppercase text-plum font-semibold">
            Gallery
          </span>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl text-plum-deep mt-4">
            Recent work
          </h1>
          <p className="mt-5 text-foreground/70 max-w-xl mx-auto leading-relaxed">
            A glimpse of treatments freshly finished in {BRAND.area}. Tap any image
            to view it larger.
          </p>
        </header>

        {categories.length > 2 && (
          <div className="flex flex-wrap gap-2 justify-center mb-10">
            {categories.map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                data-testid={`gallery-filter-${c}`}
                className={`text-xs uppercase tracking-[0.15em] px-4 py-2 rounded-full border transition ${
                  filter === c
                    ? "bg-plum text-white border-plum"
                    : "bg-white text-foreground/65 border-blush-deep/50 hover:text-plum"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        )}

        {loading && <p className="text-center text-foreground/55">Loading…</p>}

        {!loading && filtered.length === 0 && (
          <div className="bg-white rounded-3xl border border-blush-deep/40 p-14 text-center">
            <Camera className="mx-auto text-plum mb-4" size={28} />
            <h3 className="font-display text-2xl text-plum-deep">Gallery coming soon</h3>
            <p className="text-foreground/60 mt-2 max-w-md mx-auto">
              Deborah is adding photos of her recent treatments. In the meantime, you can{" "}
              <Link to="/contact" className="text-plum underline underline-offset-4">
                send an enquiry
              </Link>
              .
            </p>
          </div>
        )}

        {filtered.length > 0 && (
          <div className="columns-1 sm:columns-2 lg:columns-3 gap-5 [column-fill:_balance]">
            {filtered.map((item) => (
              <button
                key={item.id}
                type="button"
                onClick={() => setLightbox(item)}
                data-testid={`gallery-item-${item.id}`}
                className="mb-5 break-inside-avoid group block w-full text-left"
              >
                <div className="relative overflow-hidden rounded-2xl border border-blush-deep/40 bg-white">
                  <img
                    src={resolveUrl(item.image_url)}
                    alt={item.caption || "Beautify treatment"}
                    className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                  />
                  {(item.caption || item.category) && (
                    <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-plum-deep/85 via-plum-deep/40 to-transparent text-white p-4">
                      {item.category && (
                        <div className="text-[10px] tracking-[0.25em] uppercase text-champagne mb-1">
                          {item.category}
                        </div>
                      )}
                      {item.caption && <div className="text-sm">{item.caption}</div>}
                    </div>
                  )}
                </div>
              </button>
            ))}
          </div>
        )}

        <section
          data-testid="gallery-description"
          className="mt-16 max-w-3xl mx-auto text-center"
        >
          <h2 className="font-display text-2xl sm:text-3xl text-plum-deep">
            Inside the Beautify studios
          </h2>
          <div className="mt-4 space-y-4 text-foreground/70 leading-relaxed">
            <p>
              These photos are taken in Deborah's two Wadebridge studios — at Unit
              20, Dunveth Business Park and at Elixir Health on Molesworth Street.
              You'll see freshly finished lash lifts and tints, brow shaping,
              organic Dr. Hauschka facials, manicures with Spa Ritual plant-based
              polishes, and the calm treatment rooms where relaxing Swedish, hot
              stone and aromatherapy massages take place.
            </p>
            <p>
              Every treatment is tailored to the individual client, so no two
              results look quite the same. Fancy your own moment of calm? Browse
              the full{" "}
              <Link to="/treatments" className="text-plum font-semibold hover:text-plum-light transition">
                treatment &amp; price list
              </Link>{" "}
              or{" "}
              <Link to="/contact" className="text-plum font-semibold hover:text-plum-light transition">
                send Deborah a booking enquiry
              </Link>
              .
            </p>
          </div>
        </section>

        <div className="text-center mt-16">
          <Link
            to="/contact"
            data-testid="gallery-cta"
            className="inline-flex items-center gap-2 rounded-full bg-plum text-white px-8 py-4 text-sm font-medium hover:bg-plum-light transition-all hover:-translate-y-0.5 shadow-md"
          >
            Book your treatment <ArrowRight size={16} />
          </Link>
        </div>
      </div>

      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-plum-deep/85 backdrop-blur-sm p-4"
          onClick={() => setLightbox(null)}
          data-testid="lightbox"
        >
          <div onClick={(e) => e.stopPropagation()} className="max-w-4xl w-full">
            <img
              src={resolveUrl(lightbox.image_url)}
              alt={lightbox.caption || ""}
              className="w-full max-h-[80vh] object-contain rounded-2xl"
            />
            {(lightbox.caption || lightbox.category) && (
              <div className="text-center mt-4 text-white">
                {lightbox.category && (
                  <div className="text-[10px] tracking-[0.3em] uppercase text-champagne">{lightbox.category}</div>
                )}
                {lightbox.caption && <div className="mt-1 font-display text-2xl">{lightbox.caption}</div>}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
